import axios, { type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import type {
  Tenant,
  Team,
  PlayerProfile,
  Event,
  ServiceId,
  ServicePlan,
  NotificationItem,
  PlayerPaymentRecord,
  TenantBillingSubscriptionResponse,
  TenantPaymentMethod,
  TenantSubscriptionItem,
  StripePortalSessionResponse,
  StripePaymentMethodSetupResponse,
  StripeCheckoutSessionResponse,
  TenantMember,
  UserRole,
  ServiceSeatAllocation,
  InvoiceItem,
  SeatLimitExceededError,
  PaymentType,
  TrainingLog,
  VideoTelemetry,
} from '@/types/models';
import {
  INITIAL_TENANTS,
  INITIAL_TEAMS,
  INITIAL_PLAYERS,
  INITIAL_EVENTS,
  INITIAL_VIDEOS,
  INITIAL_TRAINING_LOGS,
  INITIAL_NOTIFICATIONS,
  AVAILABLE_SERVICES,
  INITIAL_PLAYER_PAYMENTS,
  INITIAL_MEMBERS,
  INITIAL_SEAT_ALLOCATIONS,
  INITIAL_INVOICES,
} from './mockData';

/**
 * Global token & org getter for Clerk session token & Tenant scoping
 */
let clerkTokenGetter: (() => Promise<string | null>) | null = null;
let activeTenantIdGetter: (() => string | null) | null = null;

export const setAuthToken = (token: string | null) => {
  if (typeof window === 'undefined') return;
  if (token) {
    localStorage.setItem('nbc_auth_token', token);
    localStorage.setItem('auth_token', token);
  } else {
    localStorage.removeItem('nbc_auth_token');
    localStorage.removeItem('auth_token');
  }
};

export const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('nbc_auth_token') || localStorage.getItem('auth_token') || null;
};

export const hasValidSessionOrToken = (): boolean => {
  if (typeof window === 'undefined') return false;

  const pathname = window.location.pathname;
  const hash = window.location.hash;
  if (
    pathname.startsWith('/auth') ||
    pathname === '/register-tenant' ||
    hash.includes('/auth') ||
    hash.includes('login') ||
    hash.includes('register-tenant')
  ) {
    return false;
  }

  const clerk = (window as any).Clerk;
  if (clerk && typeof clerk.isReady === 'function' && clerk.isReady()) {
    return Boolean(clerk.session && clerk.user);
  }

  const token = getAuthToken();
  return Boolean(
    token &&
    token !== 'null' &&
    token !== 'undefined' &&
    token.trim() !== '' &&
    !token.startsWith('bearer_session_')
  );
};

export const registerClerkAuthProviders = (
  tokenFn: () => Promise<string | null>,
  tenantIdFn: () => string | null
) => {
  clerkTokenGetter = tokenFn;
  activeTenantIdGetter = tenantIdFn;
};

// API Base URL from environment or default to /api/v1
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
export const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || '';

// Circuit breaker state to prevent spamming unreachable local servers
let isBackendAvailable: boolean | null = null;
let lastCheckTime = 0;
const CHECK_INTERVAL = 30000;

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 4000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Request Interceptor: Attach Clerk Bearer Token & Active Tenant Header (Self-Managed Context)
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      let token: string | null = null;

      // 1. Check window.Clerk session if available
      if (typeof window !== 'undefined' && (window as any).Clerk?.session) {
        try {
          token = await (window as any).Clerk.session.getToken();
          if (token) {
            setAuthToken(token);
          }
        } catch (e) {
          // fallback
        }
      }

      // 2. Token getter
      if (!token && clerkTokenGetter) {
        token = await clerkTokenGetter();
      }

      // 3. Persistent localStorage token
      if (!token) {
        token = getAuthToken();
      }

      if (token && !token.startsWith('bearer_session_')) {
        config.headers.set('Authorization', token.startsWith('Bearer ') ? token : `Bearer ${token}`);
      }

      // 4. Attach active tenant UUID for backend self-managed tenant scoping
      let tenantId: string | null = null;
      if (activeTenantIdGetter) {
        tenantId = activeTenantIdGetter();
      }
      if (!tenantId && typeof window !== 'undefined') {
        tenantId = localStorage.getItem('nbc_active_tenant_id') || localStorage.getItem('active_org_id');
      }

      if (tenantId) {
        config.headers.set('X-Tenant-Id', tenantId);
      }

      // 5. Active user ID header if present
      if (typeof window !== 'undefined') {
        const storedUserId = localStorage.getItem('nbc_user_id') || localStorage.getItem('user_id');
        if (storedUserId) {
          config.headers.set('X-User-Id', storedUserId);
        }
      }
    } catch (err) {
      // ignore auth header error
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor: Handle 401 Unauthorized & 403 Forbidden multi-tenant barriers
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    isBackendAvailable = true;
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const message = error.response?.data?.message || error.message;

    if (!error.response || error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      isBackendAvailable = false;
      lastCheckTime = Date.now();
    }

    if (status === 401) {
      console.warn('[ApiClient:401] Unauthorized. Redirecting to authentication...');
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
    } else if (status === 403) {
      console.warn('[ApiClient:403] Multi-tenant access denied:', message);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth:forbidden', { detail: { message } }));
      }
    }

    return Promise.reject(error);
  }
);

function shouldAttemptRequest(requiresAuth = true): boolean {
  if (typeof window !== 'undefined') {
    const pathname = window.location.pathname;
    const hash = window.location.hash;
    const isAuthPage =
      pathname.startsWith('/auth') ||
      pathname === '/register-tenant' ||
      pathname === '/onboarding' ||
      hash.includes('/auth') ||
      hash.includes('login') ||
      hash.includes('register-tenant');

    if (requiresAuth && (isAuthPage || !hasValidSessionOrToken())) {
      return false;
    }
  }
  if (isBackendAvailable === false) {
    if (Date.now() - lastCheckTime < CHECK_INTERVAL) {
      return false;
    }
  }
  return true;
}

// Local in-memory caches to guarantee 100% resilient fallback
const localCache = {
  tenants: [...INITIAL_TENANTS],
  teams: [...INITIAL_TEAMS],
  players: [...INITIAL_PLAYERS],
  events: [...INITIAL_EVENTS],
  videos: [...INITIAL_VIDEOS],
  trainingLogs: [...INITIAL_TRAINING_LOGS],
  notifications: [...INITIAL_NOTIFICATIONS],
  services: [...AVAILABLE_SERVICES],
  playerPayments: [...INITIAL_PLAYER_PAYMENTS],
  members: [...INITIAL_MEMBERS],
  allocations: { ...INITIAL_SEAT_ALLOCATIONS } as Record<string, ServiceSeatAllocation>,
  invoices: [...INITIAL_INVOICES],
  tenantPaymentMethod: {
    hasPaymentMethod: true,
    paymentMethodId: 'pm_123456',
    brand: 'visa',
    last4: '4242',
    expMonth: 12,
    expYear: 2028,
    isDefault: true,
    stripeCustomerId: 'cus_tokyo_gas_9x21',
  } as TenantPaymentMethod,
  tenantSubscriptions: [
    {
      id: 'sub_core_01',
      serviceId: 'BASEBALL_OPS_CORE' as ServiceId,
      serviceName: 'Baseball Cloud Core',
      status: 'ACTIVE' as const,
      payerType: 'TEAM' as PaymentType,
      billingInterval: 'MONTHLY' as const,
      amount: 79.0,
      currency: 'USD',
      currentPeriodEnd: '2025-12-31',
      stripeSubscriptionId: 'sub_core_01',
    },
    {
      id: 'sub_video_01',
      serviceId: 'VIDEO_ANALYTICS' as ServiceId,
      serviceName: 'Video Analytics & AI Replay',
      status: 'ACTIVE' as const,
      payerType: 'TEAM' as PaymentType,
      billingInterval: 'MONTHLY' as const,
      amount: 149.0,
      currency: 'USD',
      currentPeriodEnd: '2025-12-31',
      stripeSubscriptionId: 'sub_video_01',
    },
  ] as TenantSubscriptionItem[],
};

/* ========================================================================= */
/* 1. TENANTS & REGISTRATION API                                             */
/* ========================================================================= */
export interface TenantRegistrationPayload {
  userId?: string;
  orgName: string;
  tier: import('@/types/models').TenantTier;
  sportType?: string;
  seasonYear?: string;
  managerName: string;
  managerEmail: string;
  managerPhone?: string;
}

export const tenantsApi = {
  async getTenants(): Promise<Tenant[]> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.get('/tenants');
        const data = response.data?.data || response.data;
        if (Array.isArray(data) && data.length > 0) {
          localCache.tenants = data;
          return data;
        }
      } catch (err) {
        // Fallback
      }
    }
    return localCache.tenants;
  },

  async getCurrentTenant(): Promise<Tenant | null> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.get('/tenants/current');
        const data = response.data?.data || response.data;
        if (data?.id) return data;
      } catch (err) {
        // Fallback
      }
    }
    return localCache.tenants[0] || null;
  },

  /**
   * POST /api/v1/tenants/register
   * Strict spec: Form fields: orgName, tier, sportType, managerName, managerEmail, managerPhone.
   * DO NOT send orgId or slug fields to the backend!
   */
  async registerTenant(payload: TenantRegistrationPayload): Promise<any> {
    const cleanPayload: Record<string, any> = {
      orgName: payload.orgName,
      tier: payload.tier,
      sportType: payload.sportType || 'baseball',
      managerName: payload.managerName,
      managerEmail: payload.managerEmail,
    };
    if (payload.userId) cleanPayload.userId = payload.userId;
    if (payload.managerPhone) cleanPayload.managerPhone = payload.managerPhone;
    if (payload.seasonYear) cleanPayload.seasonYear = payload.seasonYear;

    if (shouldAttemptRequest(false)) {
      try {
        const response = await apiClient.post('/tenants/register', cleanPayload);
        const data = response.data?.data || response.data;
        const tenant = data?.tenant || data;

        if (tenant?.id) {
          const registeredTenant: Tenant = {
            id: tenant.id,
            name: tenant.name || payload.orgName,
            slug: tenant.slug,
            tier: tenant.tier || payload.tier,
            type: tenant.type || payload.tier,
            sportType: tenant.sportType || payload.sportType || 'baseball',
            seasonYear: tenant.seasonYear || payload.seasonYear,
            stripeCustomerId: tenant.stripeCustomerId || `cus_${Date.now()}`,
          };
          const idx = localCache.tenants.findIndex(t => t.id === registeredTenant.id);
          if (idx !== -1) {
            localCache.tenants[idx] = registeredTenant;
          } else {
            localCache.tenants.unshift(registeredTenant);
          }
        }
        return response.data;
      } catch (error: any) {
        if (error.response?.data?.message) {
          throw new Error(error.response.data.message);
        }
      }
    }

    // Local resilient simulation
    const generatedTenantId = `${Math.floor(10000000 + Math.random() * 90000000)}-${Date.now().toString(16)}-4000-8000-${Math.floor(100000000000 + Math.random() * 900000000000)}`;
    const newTenant: Tenant = {
      id: generatedTenantId,
      name: payload.orgName,
      slug: payload.orgName.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      tier: payload.tier,
      type: payload.tier,
      sportType: payload.sportType || 'baseball',
      seasonYear: payload.seasonYear || '2025 Spring',
      stripeCustomerId: `cus_${generatedTenantId.slice(0, 8)}`,
      stripeId: `cus_${generatedTenantId.slice(0, 8)}`,
      stripeAccountId: `cus_${generatedTenantId.slice(0, 8)}`,
    };
    localCache.tenants.unshift(newTenant);

    // Also register manager as primary ADMIN member
    const newMember: TenantMember = {
      id: `mem_${Date.now()}`,
      tenantId: generatedTenantId,
      userId: payload.userId || `user_clerk_${Date.now()}`,
      email: payload.managerEmail,
      fullName: payload.managerName,
      role: 'ADMIN',
      createdAt: new Date().toISOString(),
      isActive: true,
    };
    localCache.members.unshift(newMember);

    return {
      success: true,
      data: {
        tenant: newTenant,
        manager: {
          id: newMember.userId,
          fullName: newMember.fullName,
          email: newMember.email,
          role: 'ADMIN',
        },
      },
      message: 'Tenant and Manager created successfully.',
    };
  },
};

export const registerTenantAndManager = tenantsApi.registerTenant;

/* ========================================================================= */
/* 2. TENANT MEMBERS & USER MANAGEMENT API                                   */
/* ========================================================================= */
export const membersApi = {
  async getMembers(tenantId?: string): Promise<TenantMember[]> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.get('/tenants/members', {
          params: tenantId ? { tenantId } : undefined,
        });
        const data = response.data?.data || response.data;
        if (Array.isArray(data)) {
          localCache.members = data;
          return data;
        }
      } catch (err) {
        // Fallback
      }
    }
    return tenantId ? localCache.members.filter(m => m.tenantId === tenantId) : localCache.members;
  },

  /**
   * POST /api/v1/tenants/invitations
   * Invites new members by email and role (ADMIN, COACH, PLAYER, GUARDIAN)
   */
  async inviteMembers(invitations: { email: string; role: UserRole; teamId?: string }[]): Promise<any> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.post('/tenants/invitations', { invitations });
        return response.data;
      } catch (err: any) {
        if (err.response?.data?.message) {
          throw new Error(err.response.data.message);
        }
      }
    }

    // Local simulation
    const added: TenantMember[] = [];
    for (const inv of invitations) {
      const mem: TenantMember = {
        id: `mem_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        tenantId: localCache.tenants[0]?.id || '11111111-2222-3333-4444-555555555555',
        userId: `usr_${Date.now()}`,
        email: inv.email,
        fullName: inv.email.split('@')[0].replace(/[._]/g, ' '),
        role: inv.role,
        teamId: inv.teamId,
        createdAt: new Date().toISOString(),
        isActive: true,
      };
      localCache.members.push(mem);
      added.push(mem);
    }
    return { success: true, message: `Sent ${added.length} invitations.`, data: added };
  },

  /**
   * PATCH /api/v1/tenants/members/:memberId
   * Updates member role. Guardrail: Must verify at least one active tenant ADMIN remains.
   */
  async updateMemberRole(memberId: string, newRole: UserRole, tenantId?: string): Promise<TenantMember> {
    const targetTenantId = tenantId || localCache.tenants[0]?.id;
    const adminCount = localCache.members.filter(
      m => (!targetTenantId || m.tenantId === targetTenantId) && m.role === 'ADMIN' && m.isActive !== false
    ).length;
    const target = localCache.members.find(m => m.id === memberId);

    if (target && target.role === 'ADMIN' && newRole !== 'ADMIN' && adminCount <= 1) {
      throw new Error('Action blocked by guardrail: Organization must maintain at least one active Tenant Administrator.');
    }

    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.patch(`/tenants/members/${memberId}`, { role: newRole });
        return response.data?.data || response.data;
      } catch (err: any) {
        if (err.response?.data?.message) {
          throw new Error(err.response.data.message);
        }
      }
    }

    if (target) {
      target.role = newRole;
      return target;
    }
    throw new Error('Member not found');
  },

  /**
   * DELETE /api/v1/tenants/members/:memberId
   * Last Admin Guardrail: Raises 400 Bad Request if attempting to delete final remaining tenant ADMIN.
   * Seat Reallocation: Automatically unassigns any tenant-paid service seats returning them to pool.
   */
  async removeMember(memberId: string, tenantId?: string): Promise<{ success: boolean; message: string }> {
    const targetTenantId = tenantId || localCache.tenants[0]?.id;
    const adminCount = localCache.members.filter(
      m => (!targetTenantId || m.tenantId === targetTenantId) && m.role === 'ADMIN' && m.isActive !== false
    ).length;
    const target = localCache.members.find(m => m.id === memberId);

    if (target && target.role === 'ADMIN' && adminCount <= 1) {
      const err: any = new Error('Cannot revoke access for the last remaining Tenant Administrator.');
      err.response = {
        status: 400,
        data: { errorCode: 'LAST_ADMIN_GUARDRAIL', message: 'Cannot revoke access for the last remaining Tenant Administrator.' },
      };
      throw err;
    }

    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.delete(`/tenants/members/${memberId}`);
        return response.data;
      } catch (err: any) {
        if (err.response?.data?.message) {
          throw new Error(err.response.data.message);
        }
      }
    }

    // Soft deletion: mark isActive = false & unassign seats
    if (target) {
      target.isActive = false;
      // Seat reallocation: remove from all allocations
      Object.keys(localCache.allocations).forEach((sId) => {
        const alloc = localCache.allocations[sId];
        const assignedIdx = alloc.assignedMembers.findIndex(am => am.memberId === memberId);
        if (assignedIdx !== -1) {
          alloc.assignedMembers.splice(assignedIdx, 1);
          alloc.allocatedSeats = Math.max(0, alloc.allocatedSeats - 1);
          alloc.availableSeats = Math.max(0, alloc.totalSeats - alloc.allocatedSeats);
        }
      });
      localCache.members = localCache.members.filter(m => m.id !== memberId);
    }

    return {
      success: true,
      message: 'Member access revoked successfully. Assigned seats returned to available capacity.',
    };
  },
};

/* ========================================================================= */
/* 3. TEAMS API                                                              */
/* ========================================================================= */
export const teamsApi = {
  async getTeams(tenantId?: string): Promise<Team[]> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.get('/teams', {
          params: tenantId ? { tenantId } : undefined,
        });
        const data = response.data?.data || response.data;
        if (Array.isArray(data) && data.length > 0) {
          localCache.teams = data;
          return data;
        }
      } catch (err) {
        // Fallback
      }
    }
    return tenantId ? localCache.teams.filter(t => t.tenantId === tenantId) : localCache.teams;
  },

  async createTeam(teamData: Partial<Team>): Promise<Team> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.post('/teams', teamData);
        const created = response.data?.data || response.data;
        if (created?.id) {
          localCache.teams.push(created);
          return created;
        }
      } catch (err) {}
    }
    const newTeam: Team = {
      id: teamData.id || `team_${Date.now()}`,
      tenantId: teamData.tenantId || localCache.tenants[0]?.id,
      name: teamData.name || 'New Team',
      season: teamData.season || '2025 Spring',
      division: teamData.division || 'Varsity',
      headCoach: teamData.headCoach || 'Coach',
      color: teamData.color || '#0284c7',
      wins: 0,
      losses: 0,
      ties: 0,
      playerCount: 0,
      paymentType: teamData.paymentType || 'TEAM',
      subscriptions: teamData.subscriptions || [
        {
          serviceId: 'BASEBALL_OPS_CORE',
          status: 'ACTIVE',
          paymentType: 'TEAM',
          currentPeriodEnd: '2025-12-31',
        },
      ],
    };
    localCache.teams.push(newTeam);
    return newTeam;
  },

  async updateTeam(teamId: string, payload: Partial<Team>): Promise<Team | null> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.put(`/teams/${teamId}`, payload);
        return response.data?.data || response.data;
      } catch (err) {}
    }
    const idx = localCache.teams.findIndex(t => t.id === teamId);
    if (idx !== -1) {
      localCache.teams[idx] = { ...localCache.teams[idx], ...payload };
      return localCache.teams[idx];
    }
    return null;
  },
};

/* ========================================================================= */
/* 4. PLAYERS & ROSTER API                                                   */
/* ========================================================================= */
export const playersApi = {
  async getPlayers(teamId?: string, tenantId?: string): Promise<PlayerProfile[]> {
    if (shouldAttemptRequest()) {
      try {
        const endpoint = teamId ? `/teams/${teamId}/roster` : '/players';
        const response = await apiClient.get(endpoint);
        const data = response.data?.data || response.data;
        if (Array.isArray(data) && data.length > 0) {
          localCache.players = data;
          return data;
        }
      } catch (err) {}
    }
    return localCache.players.filter(p => (!tenantId || p.tenantId === tenantId) && (!teamId || p.teamId === teamId));
  },

  async createPlayer(teamId: string, player: Partial<PlayerProfile>): Promise<PlayerProfile> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.post(`/teams/${teamId}/roster`, player);
        const created = response.data?.data || response.data;
        if (created?.id) {
          localCache.players.push(created);
          return created;
        }
      } catch (err) {}
    }
    const newPlayer: PlayerProfile = {
      id: player.id || `ply_${Date.now()}`,
      tenantId: player.tenantId || localCache.tenants[0]?.id,
      teamId: teamId,
      firstName: player.firstName || '',
      lastName: player.lastName || '',
      jerseyNumber: player.jerseyNumber || 1,
      position: player.position || 'SS',
      secondaryPosition: player.secondaryPosition,
      bats: player.bats || 'R',
      throws: player.throws || 'R',
      status: player.status || 'ACTIVE',
      email: player.email,
      phone: player.phone,
      graduationYear: player.graduationYear || 2026,
      individualDuesStatus: player.individualDuesStatus || 'PENDING',
    };
    localCache.players.push(newPlayer);
    return newPlayer;
  },

  async updatePlayer(playerId: string, payload: Partial<PlayerProfile>): Promise<PlayerProfile | null> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.patch(`/players/${playerId}`, payload);
        return response.data?.data || response.data;
      } catch (err) {}
    }
    const idx = localCache.players.findIndex(p => p.id === playerId);
    if (idx !== -1) {
      localCache.players[idx] = { ...localCache.players[idx], ...payload };
      return localCache.players[idx];
    }
    return null;
  },

  async deletePlayer(playerId: string): Promise<boolean> {
    if (shouldAttemptRequest()) {
      try {
        await apiClient.delete(`/players/${playerId}`);
      } catch (err) {}
    }
    localCache.players = localCache.players.filter(p => p.id !== playerId);
    return true;
  },
};

/* ========================================================================= */
/* 5. BILLING, SEAT ALLOCATION & STRIPE API                                  */
/* ========================================================================= */
export const servicesApi = {
  async getServices(): Promise<ServicePlan[]> {
    if (shouldAttemptRequest(false)) {
      try {
        const response = await apiClient.get('/billing/services').catch(() => apiClient.get('/services'));
        const data = response.data?.data || response.data;
        if (Array.isArray(data) && data.length > 0) {
          localCache.services = data;
          return data;
        }
      } catch (err) {}
    }
    return [...localCache.services];
  },

  async getService(serviceId: ServiceId): Promise<ServicePlan | null> {
    const list = await this.getServices();
    return list.find(s => s.id === serviceId) || null;
  },
};

export const billingApi = {
  getServices: () => servicesApi.getServices(),
  getService: (serviceId: ServiceId) => servicesApi.getService(serviceId),

  /**
   * GET /api/v1/billing/subscription
   */
  async getTenantSubscription(): Promise<TenantBillingSubscriptionResponse> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.get('/billing/subscription');
        const data = response.data?.data || response.data;
        if (data) {
          if (data.paymentMethod) localCache.tenantPaymentMethod = data.paymentMethod;
          if (Array.isArray(data.subscriptions)) localCache.tenantSubscriptions = data.subscriptions;
          return data;
        }
      } catch (err) {}
    }
    return {
      hasActiveSubscription: localCache.tenantSubscriptions.some(s => s.status === 'ACTIVE' || s.status === 'cancel_wait'),
      stripeCustomerId: localCache.tenantPaymentMethod.stripeCustomerId || 'cus_tokyo_gas_9x21',
      subscriptions: [...localCache.tenantSubscriptions],
      paymentMethod: { ...localCache.tenantPaymentMethod },
    };
  },

  /**
   * GET /api/v1/billing/payment-method
   */
  async getPaymentMethod(): Promise<TenantPaymentMethod> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.get('/billing/payment-method');
        const data = response.data?.data || response.data;
        if (data) {
          localCache.tenantPaymentMethod = data;
          return data;
        }
      } catch (err) {}
    }
    return { ...localCache.tenantPaymentMethod };
  },

  /**
   * POST /api/v1/billing/portal-session
   */
  async createPortalSession(payload?: { returnUrl?: string; flow?: string }): Promise<StripePortalSessionResponse> {
    const returnUrl = payload?.returnUrl || (typeof window !== 'undefined' ? window.location.href : 'https://app.nextbasecloud.com/dashboard/billing');
    const flow = payload?.flow || 'payment_method_update';

    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.post('/billing/portal-session', { returnUrl, flow })
          .catch(() => apiClient.post('/billing/customer-portal', { returnUrl, flow }));
        const data = response.data?.data || response.data;
        if (data?.portalUrl || data?.url) {
          return { portalUrl: data.portalUrl || data.url, url: data.url || data.portalUrl };
        }
      } catch (err) {}
    }

    const mockPortalUrl = `https://billing.stripe.com/p/session/test_portal_${Date.now()}`;
    return { portalUrl: mockPortalUrl, url: mockPortalUrl };
  },

  /**
   * POST /api/v1/billing/payment-method/setup
   */
  async setupPaymentMethod(payload?: {
    paymentMethodType?: import('@/types/models').TenantPaymentMethodType;
    returnUrl?: string;
    successUrl?: string;
    cancelUrl?: string;
  }): Promise<StripePaymentMethodSetupResponse> {
    const baseUrl = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : 'https://app.nextbasecloud.com/dashboard/billing';
    const body = {
      paymentMethodType: payload?.paymentMethodType || 'card',
      returnUrl: payload?.returnUrl || baseUrl,
      successUrl: payload?.successUrl || `${baseUrl}?setup=success`,
      cancelUrl: payload?.cancelUrl || `${baseUrl}?setup=cancel`,
    };

    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.post('/billing/payment-method/setup', body)
          .catch(() => apiClient.post('/billing/payment-method', body));
        const data = response.data?.data || response.data;
        if (data) return data;
      } catch (err) {}
    }

    const sessionId = `cs_setup_${Date.now()}`;
    const mockSetupUrl = `https://checkout.stripe.com/c/pay/${sessionId}`;
    return {
      setupUrl: mockSetupUrl,
      portalUrl: mockSetupUrl,
      url: mockSetupUrl,
      sessionId,
      clientSecret: `seti_${Date.now()}_secret_${Date.now().toString(36)}`,
    };
  },

  /**
   * GET /api/v1/billing/services/:serviceId/allocations?teamId=<uuid>
   */
  async getAllocations(serviceId: ServiceId, teamId?: string): Promise<ServiceSeatAllocation> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.get(`/billing/services/${serviceId}/allocations`, {
          params: teamId ? { teamId } : undefined,
        });
        const data = response.data?.data || response.data;
        if (data) return data;
      } catch (err) {}
    }

    const existing = localCache.allocations[serviceId];
    if (existing) return existing;

    const plan = localCache.services.find(s => s.id === serviceId);
    const total = plan?.contractedSeats || 15;
    return {
      serviceId,
      serviceName: plan?.name || serviceId,
      totalSeats: total,
      allocatedSeats: 0,
      availableSeats: total,
      assignedMembers: [],
    };
  },

  /**
   * POST /api/v1/billing/services/:serviceId/assign
   * Assigns target members to an active paid service.
   * On capacity exceed, returns 409 Conflict with errorCode: "SEAT_LIMIT_EXCEEDED" and canUpgrade boolean.
   */
  async assignSeats(
    serviceId: ServiceId,
    memberIds: string[],
    userRole: UserRole = 'ADMIN',
    teamId?: string
  ): Promise<{ success: boolean; allocation: ServiceSeatAllocation }> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.post(`/billing/services/${serviceId}/assign`, { memberIds, teamId });
        return response.data?.data || response.data;
      } catch (err: any) {
        if (err.response?.status === 409 && err.response?.data) {
          const errPayload = err.response.data;
          const capacityErr: any = new Error(errPayload.message || 'Allocation exceeds contracted seat capacity.');
          capacityErr.response = err.response;
          capacityErr.seatLimitError = errPayload;
          throw capacityErr;
        }
        if (err.response?.data?.message) {
          throw new Error(err.response.data.message);
        }
      }
    }

    // Local simulation with capacity validation
    const alloc = localCache.allocations[serviceId] || (await this.getAllocations(serviceId, teamId));
    const alreadyAssignedCount = alloc.allocatedSeats;
    const requestedCount = memberIds.filter(id => !alloc.assignedMembers.some(m => m.memberId === id)).length;

    if (alreadyAssignedCount + requestedCount > alloc.totalSeats) {
      const canUpgrade = userRole === 'ADMIN' || userRole === 'ORG_ADMIN';
      const seatErrPayload: SeatLimitExceededError = {
        errorCode: 'SEAT_LIMIT_EXCEEDED',
        message: 'Allocation exceeds contracted seat capacity.',
        totalSeats: alloc.totalSeats,
        allocatedSeats: alloc.allocatedSeats,
        requestedSeats: requestedCount,
        canUpgrade,
      };
      const errorObj: any = new Error(seatErrPayload.message);
      errorObj.response = { status: 409, data: seatErrPayload };
      errorObj.seatLimitError = seatErrPayload;
      throw errorObj;
    }

    // Allocate seats
    memberIds.forEach((mId) => {
      if (!alloc.assignedMembers.some(m => m.memberId === mId)) {
        const member = localCache.members.find(m => m.id === mId);
        const player = localCache.players.find(p => p.id === mId);
        alloc.assignedMembers.push({
          memberId: mId,
          userId: member?.userId || player?.linkedUserId || `usr_${mId}`,
          fullName: member?.fullName || `${player?.firstName || 'Member'} ${player?.lastName || ''}`.trim(),
          email: member?.email || player?.email || 'user@example.com',
          role: member?.role || 'PLAYER',
          jerseyNumber: player?.jerseyNumber,
          teamId: member?.teamId || player?.teamId || teamId,
          assignedAt: new Date().toISOString(),
        });
      }
    });

    alloc.allocatedSeats = alloc.assignedMembers.length;
    alloc.availableSeats = Math.max(0, alloc.totalSeats - alloc.allocatedSeats);
    localCache.allocations[serviceId] = alloc;

    return { success: true, allocation: alloc };
  },

  /**
   * POST /api/v1/billing/services/:serviceId/unassign
   * Error 400 Bad Request with errorCode: "NO_SERVICE_ASSIGNED" and message: "サービスが割り当てられてません"
   */
  async unassignSeats(
    serviceId: ServiceId,
    memberIds: string[]
  ): Promise<{ success: boolean; allocation: ServiceSeatAllocation }> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.post(`/billing/services/${serviceId}/unassign`, { memberIds });
        return response.data?.data || response.data;
      } catch (err: any) {
        if (err.response?.status === 400 && err.response?.data?.errorCode === 'NO_SERVICE_ASSIGNED') {
          const customErr: any = new Error(err.response.data.message || 'サービスが割り当てられてません');
          customErr.response = err.response;
          throw customErr;
        }
        if (err.response?.data?.message) {
          throw new Error(err.response.data.message);
        }
      }
    }

    const alloc = localCache.allocations[serviceId];
    if (!alloc) {
      const err: any = new Error('サービスが割り当てられてません');
      err.response = { status: 400, data: { errorCode: 'NO_SERVICE_ASSIGNED', message: 'サービスが割り当てられてません' } };
      throw err;
    }

    const unassignedCount = alloc.assignedMembers.filter(m => memberIds.includes(m.memberId)).length;
    if (unassignedCount === 0) {
      const err: any = new Error('サービスが割り当てられてません');
      err.response = { status: 400, data: { errorCode: 'NO_SERVICE_ASSIGNED', message: 'サービスが割り当てられてません' } };
      throw err;
    }

    alloc.assignedMembers = alloc.assignedMembers.filter(m => !memberIds.includes(m.memberId));
    alloc.allocatedSeats = alloc.assignedMembers.length;
    alloc.availableSeats = Math.max(0, alloc.totalSeats - alloc.allocatedSeats);
    localCache.allocations[serviceId] = alloc;

    return { success: true, allocation: alloc };
  },

  /**
   * POST /api/v1/billing/subscriptions/:id/cancel
   * Transitions status to cancel_wait with badge "Pending Cancellation (Active until Period End)"
   */
  async cancelSubscription(subscriptionId: string): Promise<TenantSubscriptionItem> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.post(`/billing/subscriptions/${subscriptionId}/cancel`);
        return response.data?.data || response.data;
      } catch (err: any) {
        if (err.response?.data?.message) {
          throw new Error(err.response.data.message);
        }
      }
    }

    const sub = localCache.tenantSubscriptions.find(s => s.id === subscriptionId || s.serviceId === (subscriptionId as any));
    if (sub) {
      sub.status = 'cancel_wait';
      sub.canResume = true;
      return sub;
    }
    throw new Error('Subscription not found');
  },

  /**
   * POST /api/v1/billing/subscriptions/:id/resume
   * Resumes subscription from cancel_wait back to ACTIVE
   */
  async resumeSubscription(subscriptionId: string): Promise<TenantSubscriptionItem> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.post(`/billing/subscriptions/${subscriptionId}/resume`);
        return response.data?.data || response.data;
      } catch (err: any) {
        if (err.response?.data?.message) {
          throw new Error(err.response.data.message);
        }
      }
    }

    const sub = localCache.tenantSubscriptions.find(s => s.id === subscriptionId || s.serviceId === (subscriptionId as any));
    if (sub) {
      sub.status = 'ACTIVE';
      sub.canResume = false;
      return sub;
    }
    throw new Error('Subscription not found');
  },

  /**
   * GET /api/v1/billing/invoices
   * Retrieves invoices with direct pdfUrl download links, filtered by payerScope ('TEAM' or 'INDIVIDUAL')
   */
  async getInvoices(payerScope?: 'TEAM' | 'INDIVIDUAL'): Promise<InvoiceItem[]> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.get('/billing/invoices', {
          params: payerScope ? { payerScope } : undefined,
        });
        const data = response.data?.data || response.data;
        if (Array.isArray(data)) {
          localCache.invoices = data;
          return data;
        }
      } catch (err) {}
    }
    return payerScope ? localCache.invoices.filter(i => i.payerScope === payerScope) : localCache.invoices;
  },

  /**
   * POST /api/v1/billing/teams/:teamId/services/:serviceId/configure
   */
  async configureService(teamId: string, serviceId: ServiceId, paymentType: PaymentType): Promise<any> {
    if (serviceId === 'VIDEO_ANALYTICS' || serviceId === 'BASEBALL_OPS_CORE') {
      if (paymentType === 'INDIVIDUAL') {
        throw new Error('Service requires TEAM payment and cannot be configured for individual player billing.');
      }
    }

    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.post(`/billing/teams/${teamId}/services/${serviceId}/configure`, {
          paymentType,
        });
        return response.data;
      } catch (err: any) {
        if (err.response?.data?.message) {
          throw new Error(err.response.data.message);
        }
      }
    }
    return { success: true, teamId, serviceId, paymentType };
  },

  /**
   * POST /api/v1/billing/teams/:teamId/services/:serviceId/subscribe-team
   */
  async subscribeTeam(teamId: string, serviceId: ServiceId, payload?: any): Promise<any> {
    const baseUrl = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : 'https://app.nextbasecloud.com/dashboard/billing';
    const body = {
      paymentMethodType: payload?.paymentMethodType || 'card',
      successUrl: payload?.successUrl || `${baseUrl}?session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: payload?.cancelUrl || baseUrl,
    };

    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.post(`/billing/teams/${teamId}/services/${serviceId}/subscribe-team`, body);
        return response.data?.data || response.data;
      } catch (err: any) {
        if (err.response?.data?.message) {
          throw new Error(err.response.data.message);
        }
      }
    }

    const existing = localCache.tenantSubscriptions.find(s => s.serviceId === serviceId);
    if (existing) {
      existing.status = 'ACTIVE';
    } else {
      const plan = localCache.services.find(s => s.id === serviceId);
      localCache.tenantSubscriptions.push({
        id: `sub_${Date.now()}`,
        serviceId,
        serviceName: plan?.name || serviceId,
        status: 'ACTIVE',
        payerType: 'TEAM',
        billingInterval: 'MONTHLY',
        amount: plan?.priceMonthly || 79,
        currency: 'USD',
        currentPeriodEnd: '2025-12-31',
        stripeSubscriptionId: `sub_stripe_${Date.now()}`,
      });
    }

    const sessionId = `cs_team_${serviceId}_${Date.now()}`;
    return {
      success: true,
      teamId,
      serviceId,
      status: 'ACTIVE',
      checkoutUrl: `https://checkout.stripe.com/c/pay/${sessionId}`,
      sessionId,
    };
  },

  /**
   * POST /api/v1/billing/checkout/individual-session
   * Mandates: Individual subscriptions (payer_type="INDIVIDUAL") MUST strictly enforce billing_interval="MONTHLY".
   */
  async createCheckoutSession(payload: {
    serviceId: ServiceId;
    teamId: string;
    playerId?: string;
    successUrl?: string;
    cancelUrl?: string;
  }): Promise<StripeCheckoutSessionResponse> {
    if (payload.serviceId === 'VIDEO_ANALYTICS' || payload.serviceId === 'BASEBALL_OPS_CORE') {
      throw new Error('This service requires TEAM_ONLY payment.');
    }

    const enforcedPayload = {
      ...payload,
      payer_type: 'INDIVIDUAL',
      billing_interval: 'MONTHLY', // Enforced monthly billing
    };

    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.post('/billing/checkout/individual-session', enforcedPayload);
        const data = response.data?.data || response.data;
        if (data?.checkoutUrl) return data;
      } catch (err: any) {
        if (err.response?.data?.message) {
          throw new Error(err.response.data.message);
        }
      }
    }
    const sessionId = `cs_live_${Date.now()}_${payload.serviceId}`;
    return {
      checkoutUrl: `https://checkout.stripe.com/c/pay/${sessionId}`,
      sessionId,
    };
  },

  async getPlayerPayments(teamId?: string, playerId?: string): Promise<PlayerPaymentRecord[]> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.get('/billing/payments', { params: { teamId, playerId } });
        const data = response.data?.data || response.data;
        if (Array.isArray(data) && data.length > 0) {
          localCache.playerPayments = data;
          return data;
        }
      } catch (err) {}
    }
    let list = [...localCache.playerPayments];
    if (playerId) list = list.filter(p => p.playerId === playerId);
    return list;
  },
};

/* ========================================================================= */
/* 6. EVENTS, VIDEOS, TRAINING & NOTIFICATIONS                               */
/* ========================================================================= */
export const eventsApi = {
  async getEvents(teamId?: string, tenantId?: string): Promise<Event[]> {
    if (shouldAttemptRequest()) {
      try {
        const endpoint = teamId ? `/teams/${teamId}/events` : '/events';
        const response = await apiClient.get(endpoint);
        const data = response.data?.data || response.data;
        if (Array.isArray(data) && data.length > 0) {
          localCache.events = data;
          return data;
        }
      } catch (err) {}
    }
    return localCache.events.filter(e => (!tenantId || e.tenantId === tenantId) && (!teamId || e.teamId === teamId));
  },

  async createEvent(eventData: Partial<Event>): Promise<Event> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.post('/events', eventData);
        const created = response.data?.data || response.data;
        if (created?.id) {
          localCache.events.push(created);
          return created;
        }
      } catch (err) {}
    }
    const created: Event = {
      id: eventData.id || `evt_${Date.now()}`,
      tenantId: eventData.tenantId || localCache.tenants[0]?.id,
      teamId: eventData.teamId || 'team_tokyo_gas_top',
      title: eventData.title || 'Event',
      eventType: eventData.eventType || 'PRACTICE',
      startTime: eventData.startTime || new Date().toISOString(),
      endTime: eventData.endTime || new Date(Date.now() + 7200000).toISOString(),
      location: eventData.location || 'Diamond Field',
      opponent: eventData.opponent,
      homeAway: eventData.homeAway || 'HOME',
      notes: eventData.notes,
      attendanceSummary: eventData.attendanceSummary || { attending: 10, declined: 0, maybe: 0, pending: 2 },
    };
    localCache.events.push(created);
    return created;
  },

  async rsvpEvent(eventId: string, playerId: string, status: 'CONFIRMED' | 'DECLINED' | 'TENTATIVE'): Promise<any> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.post(`/events/${eventId}/rsvp`, { playerId, status });
        return response.data;
      } catch (err) {}
    }
    return { success: true, eventId, playerId, status };
  },
};

export const lineupsApi = {
  async getLineup(teamId: string): Promise<any> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.get(`/teams/${teamId}/lineup`);
        return response.data?.data || response.data;
      } catch (err) {}
    }
    return null;
  },

  async saveLineup(teamId: string, lineupData: any): Promise<any> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.put(`/teams/${teamId}/lineup`, lineupData);
        return response.data;
      } catch (err) {}
    }
    return { success: true, message: 'Lineup saved locally' };
  },
};

export const videosApi = {
  async getVideos(teamId?: string, params?: { playerId?: string; tag?: string }): Promise<VideoTelemetry[]> {
    if (shouldAttemptRequest()) {
      try {
        const endpoint = teamId ? `/teams/${teamId}/videos` : '/videos';
        const response = await apiClient.get(endpoint, { params });
        const data = response.data?.data || response.data;
        if (Array.isArray(data) && data.length > 0) {
          localCache.videos = data;
          return data;
        }
      } catch (err) {}
    }
    return localCache.videos;
  },
};

export const trainingApi = {
  async getTrainingLogs(playerId?: string): Promise<TrainingLog[]> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.get('/training', { params: playerId ? { playerId } : undefined });
        const data = response.data?.data || response.data;
        if (Array.isArray(data) && data.length > 0) {
          localCache.trainingLogs = data;
          return data;
        }
      } catch (err) {}
    }
    return localCache.trainingLogs;
  },
};

export const notificationsApi = {
  async getNotifications(params?: { tenantId?: string; unreadOnly?: boolean }): Promise<NotificationItem[]> {
    if (shouldAttemptRequest()) {
      try {
        const response = await apiClient.get('/notifications', { params });
        const data = response.data?.data || response.data;
        if (Array.isArray(data)) {
          localCache.notifications = data;
          return data;
        }
      } catch (err) {}
    }
    let list = [...localCache.notifications];
    if (params?.tenantId) list = list.filter(n => !n.tenantId || n.tenantId === params.tenantId);
    if (params?.unreadOnly) list = list.filter(n => !n.read);
    return list;
  },

  async markAsRead(notificationId: string): Promise<NotificationItem | null> {
    const target = localCache.notifications.find(n => n.id === notificationId);
    if (target) {
      target.read = true;
      return { ...target };
    }
    return null;
  },

  async markAllAsRead(tenantId?: string): Promise<boolean> {
    localCache.notifications.forEach(n => {
      if (!tenantId || !n.tenantId || n.tenantId === tenantId) n.read = true;
    });
    return true;
  },
};

export default apiClient;
